import { AnyInputConfig, InputType, useTracking } from '@flock/shared-ui'
import { useMediaQuery, useTheme } from '@mui/material'
import { useState } from 'react'
import { useMutation } from '@apollo/client'
import { createLeadHelper, localStore, UserEventType } from '@flock/utils'
import {
  Core_TransactionType,
  LandingCreateLeadDocument,
} from '@flock/flock-gql-server/src/__generated__/graphql'
import { getEventTokenUUID } from '../../../utils/analytics'
import { useCalendly } from '../../utils'
import { BOOK_A_DEMO_CALENDLY, FLOCK_WEBINAR_241022 } from '../../../constants'

export type FormResult = {
  fullName: string
  phoneNumber: string
  email: string
}

const useWebinarModal = () => {
  // Presentational layout hooks
  const { track } = useTracking()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'))
  const [webinarModalOpen, setWebinarModalOpen] = useState(false)
  const [successModalOpen, setSuccessModalOpen] = useState(false)
  const onSubmitDetails = () => {
    setSuccessModalOpen(true)
  }
  const [onboardingLeadUrlParams, setOnboardingLeadUrlParams] =
    useState<string>('')

  const { onOpenCalendly, ...otherCalendlyProps } = useCalendly({
    utmMedium: 'book-a-demo',
    scheduleUrl: BOOK_A_DEMO_CALENDLY as string,
  })

  const [loading, setLoading] = useState(false)
  const [createLead] = useMutation(LandingCreateLeadDocument)

  const onSubmit = async (result: any) => {
    try {
      setLoading(true)

      const leadData = {
        contactInfo: {
          fullName: result.fullName,
          email: result.email,
          phoneNumber: result.phoneNumber,
        },
        answers: JSON.stringify({
          isWebinarViewRequest: true,
        }),
        transactionType: Core_TransactionType.TransactionTypeContactInformation,
        eventTokenUuid: getEventTokenUUID(),
      }

      const queryParams = localStore?.getItem('queryParams')
      let parsedQueryParams: any = {}
      if (queryParams) {
        parsedQueryParams = JSON.parse(queryParams)
      }

      track('requestedMostRecentWebinar', {
        type: 'button',
        leadConversion: 'finished',
        webinarCta: true,
        actionType: UserEventType.BUTTON_CLICK,
      })
      await createLeadHelper(leadData, parsedQueryParams, createLead)

      setLoading(false)
      setWebinarModalOpen(false)
    } catch (e) {
      setLoading(false)
      setWebinarModalOpen(false)
    }
    window.open(FLOCK_WEBINAR_241022, '_blank')
  }

  const inputConfigs: AnyInputConfig[] = [
    {
      name: 'fullName',
      type: InputType.Text,
      required: true,
      props: {
        format: 'fullName',
        label: 'Your Full Name',
      },
    },
    {
      name: 'email',
      type: InputType.Text,
      required: true,
      props: {
        format: 'email',
        label: 'Your Email',
      },
    },
    {
      name: 'phoneNumber',
      type: InputType.Text,
      required: true,
      props: {
        format: 'phone',
        label: 'Your Phone Number',
      },
    },
  ]

  return {
    isMobile,
    isTablet,
    webinarModalOpen,
    setWebinarModalOpen,
    successModalOpen,
    onSubmitDetails,
    inputConfigs,
    onSubmit,
    loading,
    onboardingLeadUrlParams,
    setOnboardingLeadUrlParams,
    setSuccessModalOpen,
    onOpenCalendly,
    otherCalendlyProps,
  }
}

export default useWebinarModal
